import React, { FC, useState } from 'react';

import { Heading, Text, Types } from '@marriott/mi-ui-library';
import { SkeletonLoaderV2 } from '../../molecules/SkeletonLoaderV2';
import { MembershipTierTabSlider } from '../../molecules/MembershipTierTabSlider';
import type { MembershipTierBenefit, MembershipTiersViewProps } from './MemberShipTiersBenefits.types';
import { StyledMembershipTiersView } from './MemberShipTiersBenefits.styles';

// Add a sanitization utility function:
const sanitizeClassName = (value: string): string => {
  // Allow only alphanumeric, hyphens, and underscores
  return (value || '').replace(/[^a-zA-Z0-9_-]/g, '');
};

export const TierHero: FC<{ eyebrow: string; title: string; tagline: string }> = ({ eyebrow, title, tagline }) => (
  <div className="tier-hero d-flex flex-column align-items-center">
    <Text
      copyText={eyebrow}
      fontSize={Types.size.extraSmall}
      element={Types.tags.paragraph}
      customClass="tier-hero__eyebrow"
    />
    <Heading
      variation={Types.headingType.title}
      fontSize={Types.size.small}
      titleText={title}
      customClass="tier-hero__title"
    />
    <Text
      copyText={tagline}
      fontSize={Types.size.medium}
      element={Types.tags.paragraph}
      customClass="tier-hero__tagline t-subtitle-m"
    />
  </div>
);

export const BenefitCardItem: FC<{
  benefit: MembershipTierBenefit;
}> = ({ benefit }) => (
  <div className="benefit-card">
    <div className="benefit-card__icon-wrap">
      <span className={`icon-${sanitizeClassName(benefit.iconType)} benefit-icon`} aria-hidden />
    </div>
    <Heading
      variation={Types.headingType.subtitle}
      fontSize={Types.size.medium}
      titleText={benefit.title}
      customClass="benefit-card__title t-subtitle-m"
    />
    <div className="benefit-card__body">
      <Text
        copyText={benefit.description}
        fontSize={Types.size.small}
        element={Types.tags.span}
        customClass="benefit-card__description t-font-s"
      />
    </div>
  </div>
);

export const BenefitsGrid: FC<{
  benefits: MembershipTierBenefit[];
  tierId: string;
}> = ({ benefits, tierId }) => (
  <div className="benefits-grid">
    {benefits.map(benefit => (
      <BenefitCardItem key={`${tierId}-${benefit.title}`} benefit={benefit} />
    ))}
  </div>
);

export const MembershipTiersViewSkeleton: FC = () => (
  <StyledMembershipTiersView className="py-4 my-5 membership-tiers-skeleton" data-testid="membership-tiers-skeleton">
    <SkeletonLoaderV2 className="membership-tiers-skeleton__title mx-auto" />
    <div className="membership-tiers-skeleton__tabs d-flex justify-content-center py-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonLoaderV2 key={i} className="membership-tiers-skeleton__tab mx-2" />
      ))}
    </div>
    <div className="membership-tiers-skeleton__hero d-flex flex-column align-items-center py-5 px-3">
      <SkeletonLoaderV2 className="membership-tiers-skeleton__eyebrow" />
      <SkeletonLoaderV2 className="membership-tiers-skeleton__heading mt-3" />
      <SkeletonLoaderV2 className="membership-tiers-skeleton__tagline mt-2" />
    </div>
    <div className="benefits-grid px-3 pb-5">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="d-flex flex-column align-items-center">
          <SkeletonLoaderV2 className="membership-tiers-skeleton__icon" />
          <SkeletonLoaderV2 className="membership-tiers-skeleton__card-title mt-3" />
          <SkeletonLoaderV2 className="membership-tiers-skeleton__card-desc mt-2" />
        </div>
      ))}
    </div>
  </StyledMembershipTiersView>
);

export const AccountTableSkeleton: FC = () => (
  <div className="account-table-skeleton py-4" data-testid="account-table-skeleton">
    <SkeletonLoaderV2 className="account-table-skeleton__header mx-auto" />
    <div className="account-table-skeleton__columns d-flex justify-content-end py-3 px-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <SkeletonLoaderV2 key={i} className="account-table-skeleton__col-header mx-2" />
      ))}
    </div>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="account-table-skeleton__row d-flex align-items-center px-3 py-3">
        <SkeletonLoaderV2 className="account-table-skeleton__row-label" />
        <div className="d-flex ml-auto">
          {Array.from({ length: 5 }).map((_, j) => (
            <SkeletonLoaderV2 key={j} className="account-table-skeleton__cell mx-2" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

export const MembershipTiersView: FC<MembershipTiersViewProps> = ({
  sectionTitle,
  tierContentByCode,
  memberLevelDetails,
  eliteLabel,
  desktopColumns,
  tabletColumns,
  mobileColumns,
  tabLabel,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const activeIndex = memberLevelDetails?.length ? Math.min(selectedIndex, memberLevelDetails.length - 1) : 0;
  const selectedTier = memberLevelDetails?.[activeIndex];
  const tierContent = selectedTier?.tierName ? tierContentByCode?.[selectedTier?.tierName] : undefined;

  if (!memberLevelDetails?.length || !selectedTier || !tierContent) {
    return null;
  }

  return (
    <StyledMembershipTiersView
      data-testid="account-MembershipTiers"
      data-component-name="o-account-MembershipTiers"
      className="py-4 my-5"
    >
      <Heading
        variation={Types.headingType.title}
        fontSize={Types.size.small}
        titleText={sectionTitle}
        customClass="membership-tiers__heading d-flex justify-content-center align-items-center"
      />

      <MembershipTierTabSlider
        tiers={memberLevelDetails}
        selectedIndex={activeIndex}
        onSelect={setSelectedIndex}
        eliteLabel={eliteLabel}
        desktopColumns={desktopColumns}
        tabletColumns={tabletColumns}
        mobileColumns={mobileColumns}
        tabLabel={tabLabel}
      />

      <div
        role="tabpanel"
        id={`${selectedTier.tierName}`}
        aria-labelledby={`${selectedTier.tierName}`}
        className="membership-tiers__panel"
      >
        <TierHero eyebrow={tierContent.requirementEyebrow} title={selectedTier.name} tagline={tierContent.tagline} />

        <BenefitsGrid benefits={tierContent.benefits} tierId={selectedTier.tierName} />
      </div>
    </StyledMembershipTiersView>
  );
};
